import React, { useState, useEffect } from 'react';
import { 
  Search, 
  UserCheck, 
  UserX, 
  RefreshCw, 
  AlertCircle,
  Eye,
  Phone,
  Mail,
  User,
  Star,
  Edit,
  ArrowLeft
} from 'lucide-react';
import { isValidEmail, isValidFrenchMobile, normalizeFrenchMobile } from '../utils/validation';

export default function UsersPage() {
  const [search, setSearch] = useState('');
  const [passengers, setPassengers] = useState([]);
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  
  // Drawer states
  const [selectedUser, setSelectedUser] = useState(null); // For detail view
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  // Editing states
  const [isEditing, setIsEditing] = useState(false);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editBalance, setEditBalance] = useState('');
  const [editError, setEditError] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const token = localStorage.getItem('adminToken');

  const fetchData = async (showIndicator = false) => {
    if (showIndicator) setRefreshing(true);
    else setLoading(true);
    setError('');

    try {
      const passRes = await fetch('/api/admin/users/passengers', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!passRes.ok) throw new Error('Erreur lors du chargement des passagers');
      const passData = await passRes.json();
      setPassengers(passData);
    } catch (err) {
      console.error(err);
      setError('Impossible de charger la liste des clients. Vérifiez votre connexion.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter passengers
  const getFilteredData = () => {
    const term = search.toLowerCase().trim();
    return passengers.filter(p => 
      (p.firstName && p.firstName.toLowerCase().includes(term)) ||
      (p.lastName && p.lastName.toLowerCase().includes(term)) ||
      (p.email && p.email.toLowerCase().includes(term)) ||
      (p.phone && p.phone.includes(term))
    );
  };

  // Passenger suspension
  const handleTogglePassengerSuspension = async (passenger) => {
    const isSuspended = passenger.suspended;
    const action = isSuspended ? 'reactivate' : 'suspend';
    const confirmMsg = isSuspended 
      ? `Voulez-vous réactiver le passager ${passenger.firstName} ${passenger.lastName} ?`
      : `Voulez-vous suspendre le passager ${passenger.firstName} ${passenger.lastName} ? Ce passager ne pourra plus se connecter.`;
    
    if (!window.confirm(confirmMsg)) return;

    try {
      const res = await fetch(`/api/admin/users/passengers/${passenger.id}/${action}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      
      setPassengers(prev => prev.map(p => p.id === passenger.id ? { ...p, suspended: !isSuspended } : p));
      if (selectedUser && selectedUser.id === passenger.id) {
        setSelectedUser(prev => ({ ...prev, suspended: !isSuspended }));
      }
    } catch (err) {
      alert('Erreur lors du changement de statut du passager.');
    }
  };

  const startEditing = () => {
    setEditFirstName(selectedUser.firstName || '');
    setEditLastName(selectedUser.lastName || '');
    setEditEmail(selectedUser.email || '');
    setEditPhone(selectedUser.phone || '');
    setEditBalance(selectedUser.balance !== undefined ? selectedUser.balance : 0);
    setEditError('');
    setIsEditing(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setEditError('');

    if (!editFirstName.trim() || !editLastName.trim()) {
      setEditError('Le prénom et le nom sont obligatoires.');
      return;
    }
    if (!isValidEmail(editEmail)) {
      setEditError('Format de l\'email invalide.');
      return;
    }
    if (!editPhone.trim()) {
      setEditError('Le numéro de téléphone est obligatoire.');
      return;
    }
    if (!isValidFrenchMobile(editPhone)) {
      setEditError('Numéro de téléphone mobile français invalide (06 ou 07 uniquement).');
      return;
    }

    setSavingEdit(true);

    const normalizedPhone = normalizeFrenchMobile(editPhone);

    const body = {
      firstName: editFirstName.trim(),
      lastName: editLastName.trim(),
      email: editEmail.trim(),
      phone: normalizedPhone,
      balance: parseFloat(editBalance)
    };

    try {
      const res = await fetch(`/api/admin/users/passengers/${selectedUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Erreur lors de la mise à jour.');
      }

      setPassengers(prev => prev.map(p => p.id === selectedUser.id ? data : p));
      setSelectedUser(data);
      setSelectedUserDetails(prev => prev ? { ...prev, passenger: data } : null);
      setIsEditing(false);
    } catch (err) {
      setEditError(err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleUserSelect = async (user) => {
    setSelectedUser(user);
    setSelectedUserDetails(null);
    setIsEditing(false);
    setDetailsLoading(true);
    try {
      const res = await fetch(`/api/admin/users/passengers/${user.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setSelectedUserDetails(data);
    } catch (err) {
      console.error(err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const renderClientDetailPage = () => {
    if (!selectedUser) return null;
    
    const fullName = `${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim() || 'Sans Nom';
    const isSuspended = selectedUser.suspended;
    
    return (
      <div className="users-page-container">
        {/* Back navigation & Header */}
        <div className="users-detail-header">
          <button 
            onClick={() => setSelectedUser(null)} 
            className="btn-secondary users-back-btn"
          >
            <ArrowLeft size={16} />
            <span>Retour à la liste des clients</span>
          </button>
          
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-primary)', margin: 0 }}>
            Fiche Client 360° : {fullName}
          </h2>
        </div>

        {isEditing ? (
          <div className="glass-card users-edit-card">
            <div style={styles.editCardHeader}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '600' }}>Modifier les informations de {fullName}</h3>
            </div>
            <form onSubmit={handleSaveEdit} style={styles.editForm}>
              {editError && (
                <div style={styles.formAlertError}>
                  <AlertCircle size={16} />
                  <span>{editError}</span>
                </div>
              )}

              <div className="users-form-grid">
                <div style={styles.formGroup}>
                  <label style={styles.label}>Prénom *</label>
                  <input 
                    type="text" 
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    required
                    style={styles.formInput}
                    className="input-field"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Nom de famille *</label>
                  <input 
                    type="text" 
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    required
                    style={styles.formInput}
                    className="input-field"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Adresse Email *</label>
                  <input 
                    type="email" 
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    required
                    style={styles.formInput}
                    className="input-field"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Téléphone * (06/07)</label>
                  <input 
                    type="text" 
                    value={editPhone}
                    onChange={(e) => setEditPhone(e.target.value)}
                    required
                    placeholder="0612345678"
                    style={styles.formInput}
                    className="input-field"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Solde Portefeuille (€)</label>
                  <input 
                    type="number" 
                    step="0.01"
                    value={editBalance}
                    onChange={(e) => setEditBalance(e.target.value)}
                    style={styles.formInput}
                    className="input-field"
                  />
                </div>
              </div>

              <div className="users-edit-actions">
                <button 
                  type="button" 
                  onClick={() => setIsEditing(false)} 
                  className="btn-secondary"
                  style={{ padding: '10px 20px' }}
                >
                  Annuler
                </button>
                <button 
                  type="submit" 
                  disabled={savingEdit}
                  className="btn-primary"
                  style={{ padding: '10px 20px' }}
                >
                  {savingEdit ? 'Enregistrement...' : 'Enregistrer les modifications'}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className="users-detail-grid">
            
            {/* Left Column: Account Profile Summary */}
            <div className="users-detail-left-col">
              <div style={styles.profileSummaryCard} className="glass-card">
                <div style={styles.profileSummaryHeader}>
                  <div style={{
                    ...styles.detailLargeAvatar,
                    backgroundColor: isSuspended ? 'var(--danger-bg)' : 'rgba(0, 82, 255, 0.08)',
                    borderColor: isSuspended ? 'var(--danger-border)' : 'rgba(0, 82, 255, 0.15)'
                  }}>
                    {selectedUser.firstName ? selectedUser.firstName[0].toUpperCase() : <User size={32} />}
                  </div>
                  <h3 style={styles.detailProfileName}>{fullName}</h3>
                  <p style={styles.detailProfileRole}>Client Passager</p>
                  <div style={{ marginTop: '12px' }}>
                    {isSuspended ? (
                      <span className="status-badge danger">Compte Suspendu</span>
                    ) : (
                      <span className="status-badge success">Compte Actif</span>
                    )}
                  </div>
                </div>

                <div style={styles.profileDetailsList}>
                  <div style={styles.detailItemRow}>
                    <span style={styles.detailItemKey}>ID Client :</span>
                    <span style={styles.detailItemValMono}>{selectedUser.id}</span>
                  </div>
                  <div style={styles.detailItemRow}>
                    <span style={styles.detailItemKey}>Date d'inscription :</span>
                    <span style={styles.detailItemVal}>
                      {selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleDateString('fr-FR') : 'Non renseignée'}
                    </span>
                  </div>
                  <div style={styles.detailItemRow}>
                    <span style={styles.detailItemKey}>Statut Connexion :</span>
                    <span style={styles.detailItemVal}>
                      {selectedUser.online ? (
                        <span className="status-badge success" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'var(--success)', display: 'inline-block' }} />
                          En ligne
                        </span>
                      ) : (
                        <span className="status-badge secondary">Hors ligne</span>
                      )}
                    </span>
                  </div>
                  <div style={styles.detailItemRow}>
                    <span style={styles.detailItemKey}>Dernière connexion :</span>
                    <span style={styles.detailItemVal}>
                      {selectedUser.lastConnectionDate ? new Date(selectedUser.lastConnectionDate).toLocaleString('fr-FR') : 'Jamais connecté'}
                    </span>
                  </div>
                  <div style={styles.detailItemRow}>
                    <span style={styles.detailItemKey}>Email :</span>
                    <span style={styles.detailItemVal}>{selectedUser.email}</span>
                  </div>
                  {selectedUser.phone && (
                    <div style={styles.detailItemRow}>
                      <span style={styles.detailItemKey}>Téléphone :</span>
                      <span style={styles.detailItemVal}>{selectedUser.phone}</span>
                    </div>
                  )}
                  <div style={styles.detailItemRow}>
                    <span style={styles.detailItemKey}>Solde Portefeuille :</span>
                    <span style={{ ...styles.detailItemVal, color: 'var(--success)', fontWeight: 'bold' }}>
                      {(selectedUser.balance !== undefined ? selectedUser.balance : 0).toFixed(2)} €
                    </span>
                  </div>
                  <div style={styles.detailItemRow}>
                    <span style={styles.detailItemKey}>Note Passager :</span>
                    <span style={styles.detailItemVal}>
                      <Star size={12} fill="#eab308" color="#eab308" style={{ marginRight: '4px' }} /> 
                      {selectedUser.rating ? selectedUser.rating.toFixed(1) : '5.0'} / 5.0
                    </span>
                  </div>
                  <div style={styles.detailItemRow}>
                    <span style={styles.detailItemKey}>Code Parrainage :</span>
                    <span style={styles.detailItemValMono}>{selectedUser.referralCode || 'Aucun'}</span>
                  </div>
                </div>

                <div style={styles.profileActionBlock}>
                  <h4 style={styles.actionBlockTitle}>Actions de Gestion</h4>
                  <div style={styles.actionButtonsGrid}>
                    <button 
                      onClick={startEditing} 
                      style={styles.managerActionBtnPrimary}
                    >
                      <Edit size={14} />
                      <span>Modifier le Profil</span>
                    </button>
                    <button 
                      onClick={() => handleTogglePassengerSuspension(selectedUser)} 
                      style={{ 
                        ...styles.managerActionBtnDanger,
                        backgroundColor: isSuspended ? 'var(--success-bg)' : 'var(--danger-bg)',
                        color: isSuspended ? 'var(--success)' : 'var(--danger)',
                        borderColor: isSuspended ? 'var(--success-border)' : 'var(--danger-border)'
                      }}
                    >
                      {isSuspended ? <UserCheck size={14} /> : <UserX size={14} />}
                      <span>{isSuspended ? 'Réactiver le Compte' : 'Suspendre le Compte'}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Rides and Financial History */}
            <div className="users-detail-right-col">
              {detailsLoading ? (
                <div style={styles.detailTabLoadingContainer} className="glass-card">
                  <RefreshCw size={24} className="spin-anim" color="var(--primary)" />
                  <p style={{ marginTop: '12px', color: 'var(--text-secondary)' }}>Chargement des données du client...</p>
                </div>
              ) : selectedUserDetails ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  
                  {/* Rides History Section */}
                  <div style={styles.detailSectionCard} className="glass-card">
                    <div style={styles.detailSectionHeader}>
                      <h4 style={styles.detailSectionTitle}>Historique des Trajets ({selectedUserDetails.rides?.length || 0})</h4>
                    </div>
                    <div style={styles.detailSectionBody}>
                      {selectedUserDetails.rides && selectedUserDetails.rides.length > 0 ? (
                        <div className="users-table-scroll">
                          <table className="admin-table users-table-history">
                            <thead>
                              <tr>
                                <th>ID Course</th>
                                <th>Départ</th>
                                <th>Destination</th>
                                <th>Date / Heure</th>
                                <th>Tarif Final</th>
                                <th>Statut</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedUserDetails.rides.map(ride => (
                                <tr key={ride.id}>
                                  <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{ride.id.substring(0, 8)}...</td>
                                  <td>{ride.pickupAddress}</td>
                                  <td>{ride.destinationAddress}</td>
                                  <td>{ride.requestedAt ? new Date(ride.requestedAt).toLocaleString('fr-FR') : 'N/A'}</td>
                                  <td style={{ fontWeight: '600' }}>
                                    {ride.finalPrice ? `${ride.finalPrice.toFixed(2)} €` : 'N/A'}
                                  </td>
                                  <td>
                                    <span className={`status-badge ${
                                      ride.status === 'RIDE_COMPLETED' ? 'success' : 
                                      ride.status === 'CANCELLED' ? 'danger' : 'warning'
                                    }`}>
                                      {ride.status.replace('RIDE_', '')}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div style={styles.emptyDetailSection}>Aucune course effectuée par ce client.</div>
                      )}
                    </div>
                  </div>

                  {/* Financial History Section */}
                  <div style={styles.detailSectionCard} className="glass-card">
                    <div style={styles.detailSectionHeader}>
                      <h4 style={styles.detailSectionTitle}>Historique Financier ({selectedUserDetails.transactions?.length || 0})</h4>
                    </div>
                    <div style={styles.detailSectionBody}>
                      {selectedUserDetails.transactions && selectedUserDetails.transactions.length > 0 ? (
                        <div className="users-table-scroll">
                          <table className="admin-table users-table-history">
                            <thead>
                              <tr>
                                <th>ID Transaction</th>
                                <th>Type</th>
                                <th>Montant</th>
                                <th>Statut</th>
                                <th>Date / Heure</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedUserDetails.transactions.map(tx => (
                                <tr key={tx.id}>
                                  <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{tx.id.substring(0, 8)}...</td>
                                  <td style={{ fontWeight: '500' }}>{tx.type}</td>
                                  <td style={{ 
                                    fontWeight: '600', 
                                    color: tx.amount < 0 || tx.type === 'PAYOUT' ? 'var(--danger)' : 'var(--success)' 
                                  }}>
                                    {tx.amount < 0 || tx.type === 'PAYOUT' ? '-' : '+'}{Math.abs(tx.amount).toFixed(2)} €
                                  </td>
                                  <td>
                                    <span className={`status-badge ${
                                      tx.status === 'COMPLETED' ? 'success' : 
                                      tx.status === 'FAILED' ? 'danger' : 'warning'
                                    }`}>
                                      {tx.status}
                                    </span>
                                  </td>
                                  <td>{tx.createdAt ? new Date(tx.createdAt).toLocaleString('fr-FR') : 'N/A'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div style={styles.emptyDetailSection}>Aucune transaction enregistrée.</div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={styles.detailTabLoadingContainer} className="glass-card">
                  <AlertCircle size={24} color="var(--text-muted)" />
                  <p style={{ marginTop: '12px', color: 'var(--text-secondary)' }}>Erreur lors du chargement des détails du client.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const filteredItems = getFilteredData();

  if (loading) {
    return (
      <div style={styles.centerContainer}>
        <div style={styles.spinner}></div>
        <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>Chargement des clients...</p>
      </div>
    );
  }

  if (selectedUser) {
    return renderClientDetailPage();
  }

  return (
    <div className="users-page-container">
      <style>{`
        .users-page-container {
          display: flex;
          flex-direction: column;
          gap: 16px;
          height: 100%;
          animation: fadeIn 0.4s ease;
        }
        .users-header-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }
        .users-detail-header {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }
        .users-back-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          font-size: 0.85rem;
          font-weight: 500;
          cursor: pointer;
        }
        .users-table-scroll {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          width: 100%;
        }
        .users-admin-table {
          min-width: 620px;
          width: 100%;
        }
        .users-table-history {
          min-width: 520px;
          width: 100%;
        }
        .users-detail-grid {
          display: flex;
          gap: 20px;
          flex: 1;
          min-height: 0;
          align-items: flex-start;
        }
        .users-detail-left-col {
          flex: 0 0 320px;
          max-width: 320px;
          width: 320px;
        }
        .users-detail-right-col {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 20px;
          overflow-y: auto;
          max-height: calc(100vh - 160px);
          padding-right: 4px;
        }
        .users-edit-card {
          background-color: var(--bg-secondary);
          padding: 24px;
          max-width: 850px;
          width: 100%;
          margin: 0 auto;
        }
        .users-form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 16px;
        }
        .users-edit-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          border-top: 0.5px solid var(--border-color);
          padding-top: 20px;
          margin-top: 12px;
        }

        @media (max-width: 768px) {
          .users-header-row {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }
          .users-header-row h2 {
            font-size: 1.15rem !important;
          }
          .users-header-row button {
            width: 100%;
            justify-content: center;
          }
          .users-detail-header {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 12px !important;
          }
          .users-back-btn {
            width: 100% !important;
            justify-content: center !important;
          }
          .users-detail-grid {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 16px !important;
          }
          .users-detail-left-col {
            width: 100% !important;
            max-width: 100% !important;
            flex: 1 1 auto !important;
          }
          .users-detail-right-col {
            max-height: none !important;
            overflow-y: visible !important;
            padding-right: 0 !important;
            width: 100% !important;
          }
          .users-edit-card {
            padding: 16px !important;
            border-radius: var(--radius-sm) !important;
          }
          .users-form-grid {
            grid-template-columns: 1fr !important;
            gap: 14px !important;
          }
          .users-edit-actions {
            flex-direction: column-reverse !important;
          }
          .users-edit-actions button {
            width: 100% !important;
            justify-content: center !important;
          }
        }
      `}</style>

      {error && (
        <div style={styles.formAlertError}>
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Upper header action bar */}
      <div className="users-header-row">
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-primary)', margin: 0 }}>
            Gestion des Clients Passagers
          </h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
            {passengers.length} passager{passengers.length > 1 ? 's' : ''} inscrit{passengers.length > 1 ? 's' : ''}
          </p>
        </div>

        <button 
          onClick={() => fetchData(true)} 
          disabled={refreshing} 
          style={styles.refreshBtn}
          className="btn-secondary"
        >
          <RefreshCw size={14} className={refreshing ? 'spin-anim' : ''} />
          <span>{refreshing ? 'Actualisation...' : 'Actualiser la liste'}</span>
        </button>
      </div>

      {/* Search Bar */}
      <div style={styles.filterBar} className="glass-card">
        <Search size={16} style={styles.searchIcon} />
        <input 
          type="text" 
          placeholder="Rechercher un passager (nom, prénom, email, téléphone)..." 
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.searchInput}
          className="input-field"
        />
      </div>

      {/* Table Container */}
      <div style={styles.tablePanel} className="glass-card users-table-scroll">
        {filteredItems.length === 0 ? (
          <div style={styles.emptyTable}>
            {search ? 'Aucun passager ne correspond à votre recherche.' : 'Aucun passager inscrit.'}
          </div>
        ) : (
          <table className="admin-table users-admin-table">
            <thead>
              <tr>
                <th>Utilisateur</th>
                <th>Contact</th>
                <th>Solde / Note</th>
                <th>Statut</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => {
                const fullName = `${item.firstName || ''} ${item.lastName || ''}`.trim() || 'Sans Nom';
                const isSuspended = item.suspended;
                
                return (
                  <tr 
                    key={item.id} 
                    onClick={() => handleUserSelect(item)} 
                    style={{ 
                      cursor: 'pointer',
                      backgroundColor: selectedUser && selectedUser.id === item.id ? 'rgba(0, 82, 255, 0.04)' : 'transparent' 
                    }}
                  >
                    <td>
                      <div style={styles.userCell}>
                        <div style={{
                          ...styles.avatar,
                          backgroundColor: isSuspended ? 'var(--danger-bg)' : 'rgba(0, 82, 255, 0.08)',
                          borderColor: isSuspended ? 'var(--danger-border)' : 'rgba(0, 82, 255, 0.15)'
                        }}>
                          {item.firstName ? item.firstName[0].toUpperCase() : <User size={14} />}
                        </div>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={styles.userName}>{fullName}</span>
                            {item.online ? (
                              <span style={{ 
                                width: '8px', 
                                height: '8px', 
                                borderRadius: '50%', 
                                backgroundColor: 'var(--success)', 
                                display: 'inline-block' 
                              }} title="En ligne" />
                            ) : (
                              <span style={{ 
                                width: '8px', 
                                height: '8px', 
                                borderRadius: '50%', 
                                backgroundColor: 'var(--text-muted)', 
                                opacity: 0.5,
                                display: 'inline-block' 
                              }} title="Hors ligne" />
                            )}
                          </div>
                          <div style={styles.userId}>{item.id.substring(0, 8)}...</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={styles.contactCell}>
                        <span style={styles.contactItem}><Mail size={12} /> {item.email}</span>
                        {item.phone && <span style={styles.contactItem}><Phone size={12} /> {item.phone}</span>}
                      </div>
                    </td>
                    <td>
                      <div style={styles.ratingSolde}>
                        <span style={styles.soldeText}>{(item.balance !== undefined ? item.balance : 0).toFixed(2)} €</span>
                        <span style={styles.ratingText}>
                          <Star size={12} fill="#eab308" color="#eab308" /> 
                          {item.rating ? item.rating.toFixed(1) : '5.0'}
                        </span>
                      </div>
                    </td>
                    <td>
                      {isSuspended ? (
                        <span className="status-badge danger">Suspendu</span>
                      ) : (
                        <span className="status-badge success">Actif</span>
                      )}
                    </td>
                    <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'right' }}>
                      <div style={styles.actionsCell}>
                        <button 
                          onClick={() => handleUserSelect(item)} 
                          style={styles.actionIconBtn} 
                          title="Voir fiche 360°"
                        >
                          <Eye size={16} color="var(--primary)" />
                        </button>
                        <button 
                          onClick={() => handleTogglePassengerSuspension(item)} 
                          style={{ 
                            ...styles.actionIconBtn, 
                            backgroundColor: isSuspended ? 'var(--success-bg)' : 'var(--danger-bg)' 
                          }} 
                          title={isSuspended ? "Réactiver" : "Suspendre"}
                        >
                          {isSuspended ? <UserCheck size={16} color="var(--success)" /> : <UserX size={16} color="var(--danger)" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const styles = {
  centerContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '400px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid rgba(0, 82, 255, 0.1)',
    borderTop: '3px solid var(--primary)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  refreshBtn: {
    padding: '8px 16px',
    fontSize: '0.85rem',
  },
  filterBar: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    padding: '4px',
  },
  searchIcon: {
    position: 'absolute',
    left: '16px',
    color: 'var(--text-muted)',
    pointerEvents: 'none',
  },
  searchInput: {
    paddingLeft: '40px',
  },
  tablePanel: {
    backgroundColor: 'var(--bg-secondary)',
    borderRadius: 'var(--radius-main)',
  },
  emptyTable: {
    padding: '48px',
    textAlign: 'center',
    color: 'var(--text-secondary)',
    fontFamily: 'var(--font-title)',
  },
  userCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  avatar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    borderWidth: '0.5px',
    borderStyle: 'solid',
    fontSize: '0.85rem',
    fontWeight: '600',
    color: 'var(--primary)',
    flexShrink: 0,
  },
  userName: {
    fontWeight: '500',
    color: 'var(--text-primary)',
    fontSize: '0.9rem',
  },
  userId: {
    fontSize: '0.75rem',
    color: 'var(--text-muted)',
    fontFamily: 'monospace',
    marginTop: '2px',
  },
  contactCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  contactItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.8rem',
    color: 'var(--text-secondary)',
  },
  ratingSolde: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  soldeText: {
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  ratingText: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '0.8rem',
    color: 'var(--text-secondary)',
  },
  actionsCell: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '6px',
  },
  actionIconBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '6px',
    border: '0.5px solid var(--border-color)',
    backgroundColor: 'var(--bg-secondary)',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  profileSummaryCard: {
    padding: '24px',
    backgroundColor: 'var(--bg-secondary)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  profileSummaryHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '20px',
    borderBottom: '0.5px solid var(--border-color)',
    paddingBottom: '20px',
    width: '100%',
  },
  detailLargeAvatar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    borderWidth: '1.5px',
    borderStyle: 'solid',
    fontSize: '2rem',
    fontWeight: '600',
    color: 'var(--primary)',
    marginBottom: '16px',
  },
  detailProfileName: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  detailProfileRole: {
    fontSize: '0.85rem',
    color: 'var(--text-muted)',
    marginTop: '4px',
  },
  profileDetailsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
    borderBottom: '0.5px solid var(--border-color)',
    paddingBottom: '20px',
    marginBottom: '20px',
  },
  detailItemRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px',
    fontSize: '0.85rem',
    textAlign: 'left',
    width: '100%',
  },
  detailItemKey: {
    color: 'var(--text-secondary)',
    fontWeight: '500',
  },
  detailItemVal: {
    color: 'var(--text-primary)',
    wordBreak: 'break-all',
  },
  detailItemValMono: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    color: 'var(--text-primary)',
    wordBreak: 'break-all',
  },
  profileActionBlock: {
    width: '100%',
    textAlign: 'left',
  },
  actionBlockTitle: {
    fontSize: '0.8rem',
    fontWeight: '600',
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '12px',
  },
  actionButtonsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: '100%',
  },
  managerActionBtnPrimary: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px',
    borderRadius: 'var(--radius-sm)',
    fontSize: '0.85rem',
    fontWeight: '500',
    cursor: 'pointer',
    backgroundColor: 'rgba(0, 82, 255, 0.05)',
    color: 'var(--primary)',
    border: '0.5px solid rgba(0, 82, 255, 0.15)',
    transition: 'all 0.2s ease',
    width: '100%',
  },
  managerActionBtnDanger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px',
    borderRadius: 'var(--radius-sm)',
    fontSize: '0.85rem',
    fontWeight: '500',
    cursor: 'pointer',
    borderWidth: '0.5px',
    borderStyle: 'solid',
    transition: 'all 0.2s ease',
    width: '100%',
  },
  detailTabLoadingContainer: {
    padding: '48px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--bg-secondary)',
    height: '240px',
  },
  detailSectionCard: {
    backgroundColor: 'var(--bg-secondary)',
  },
  detailSectionHeader: {
    padding: '16px 20px',
    borderBottom: '0.5px solid var(--border-color)',
  },
  detailSectionTitle: {
    fontSize: '0.95rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  detailSectionBody: {
    padding: '20px',
  },
  emptyDetailSection: {
    padding: '24px',
    textAlign: 'center',
    color: 'var(--text-secondary)',
    fontSize: '0.85rem',
  },
  editCardHeader: {
    borderBottom: '0.5px solid var(--border-color)',
    paddingBottom: '16px',
    marginBottom: '24px',
  },
  editForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  formAlertError: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 14px',
    backgroundColor: 'var(--danger-bg)',
    border: '0.5px solid var(--danger-border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--danger)',
    fontSize: '0.85rem',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '0.85rem',
    fontWeight: '500',
    color: 'var(--text-secondary)',
  },
  formInput: {
    padding: '10px 14px',
  },
};

